/**
 * Authentication API - Login, logout, token management
 * @module api/auth
 */

import { API_BASE_URL, apiRequest } from './core.js';

// =============================================================================
// AUTHENTICATION API
// =============================================================================

/**
 * Authentication API (not using apiRequest to avoid circular auth headers)
 */
export const authAPI = {
  login: async (email, password, subdomain = null) => {
    const headers = { 'Content-Type': 'application/json' };
    if (subdomain) headers['X-Tenant-Subdomain'] = subdomain;

    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email, password }),
    });
    return response.json();
  },

  logout: async (refresh_token) => {
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token }),
    });
    return response.json();
  },

  refresh: async (refresh_token) => {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token }),
    });
    return response.json();
  },

  me: () => apiRequest('/auth/me'),

  changePassword: (oldPassword, newPassword) => apiRequest('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ oldPassword, newPassword }),
  }),

  getTenant: () => apiRequest('/auth/tenant'),

  // User preferences (theme, etc.)
  getPreferences: () => apiRequest('/auth/preferences'),

  updatePreferences: (preferences) => apiRequest('/auth/preferences', {
    method: 'PUT',
    body: JSON.stringify(preferences),
  }),
};

// =============================================================================
// SUPERADMIN API
// =============================================================================

/**
 * SuperAdmin API - Tenant management (platform-level admin only)
 * Enhanced with Commercial Cockpit endpoints
 */
export const superadminAPI = {
  // Tenants
  getTenants: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/superadmin/tenants${query ? `?${query}` : ''}`);
  },
  getTenant: (id) => apiRequest(`/superadmin/tenants/${id}`),
  createTenant: (tenant) => apiRequest('/superadmin/tenants', {
    method: 'POST',
    body: JSON.stringify(tenant),
  }),
  updateTenant: (id, tenant) => apiRequest(`/superadmin/tenants/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(tenant),
  }),

  // Tenant Users
  getTenantUsers: (tenantId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/superadmin/tenants/${tenantId}/users${query ? `?${query}` : ''}`);
  },

  // Entitlements
  getEntitlements: (tenantId) => apiRequest(`/superadmin/tenants/${tenantId}/entitlements`),
  updateEntitlements: (tenantId, items) => apiRequest(`/superadmin/tenants/${tenantId}/entitlements`, {
    method: 'PUT',
    body: JSON.stringify({ items }),
  }),

  // Invitations
  getInvitations: (tenantId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/superadmin/tenants/${tenantId}/invitations${query ? `?${query}` : ''}`);
  },
  createInvitation: (tenantId, invitation) => apiRequest(`/superadmin/tenants/${tenantId}/invitations`, {
    method: 'POST',
    body: JSON.stringify(invitation),
  }),
  revokeInvitation: (invitationId) => apiRequest(`/superadmin/invitations/${invitationId}/revoke`, {
    method: 'POST',
  }),

  // Billing
  attachStripe: (tenantId, stripeData) => apiRequest(`/superadmin/tenants/${tenantId}/billing/attach-stripe`, {
    method: 'POST',
    body: JSON.stringify(stripeData),
  }),
  suspendBilling: (tenantId, reason, effective = 'immediate') => apiRequest(`/superadmin/tenants/${tenantId}/billing/suspend`, {
    method: 'POST',
    body: JSON.stringify({ reason, effective }),
  }),
  reactivateBilling: (tenantId) => apiRequest(`/superadmin/tenants/${tenantId}/billing/reactivate`, {
    method: 'POST',
  }),

  // Audit
  getAuditLogs: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/superadmin/audit${query ? `?${query}` : ''}`);
  },

  // Impersonation & Stats
  impersonate: (userId) => apiRequest(`/superadmin/impersonate/${userId}`, {
    method: 'POST',
  }),
  getStats: () => apiRequest('/superadmin/stats'),
};
